import React, { useState, useEffect, useMemo, useCallback } from 'react';

import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Modal from 'react-bootstrap/Modal';

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons";

import { Link } from "wouter";
import dragHandleSVG from '../img/dragHandle.svg';

import { useTable } from 'react-table';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import BTable from 'react-bootstrap/Table';

import { absURL, quizTypes } from '../utils';
import QuizApi from '../QuizApi';
import AdminHeader from './AdminHeader';
import MusicQuizGenerateModal from './MusicQuizGenerateModal';

const Table = ({ columns, data, rowOrderUpdate }) => {
  const [records, setRecords] = React.useState(data)

  const getRowId = React.useCallback(row => {
    return row.id
  }, [])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    data: records,
    columns,
    getRowId,
  })

  const moveRow = (dragIndex, hoverIndex) => {
    const dragRecord = records[dragIndex]
    setRecords(
      update(records, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRecord],
        ],
      })
    )
  }

	const onRowDrop = useCallback(() => {
		rowOrderUpdate(records);
	}, [records, rowOrderUpdate]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="adminTableScroll">
        <BTable striped hover size="md" {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                <th></th>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(
              (row, index) =>
                prepareRow(row) || (
                  <Row
                    index={index}
                    row={row}
                    moveRow={moveRow}
									onRowDrop={onRowDrop}
                    {...row.getRowProps()}
                  />
                )
            )}
          </tbody>
        </BTable>
      </div>
    </DndProvider>
  )
}

const DND_ITEM_TYPE = 'row'

const Row = ({ row, index, moveRow, onRowDrop }) => {
  const dropRef = React.useRef(null)
  const dragRef = React.useRef(null)

  const [, drop] = useDrop({
    accept: DND_ITEM_TYPE,
		drop(item, monitor) {
			onRowDrop(item);
		},
    hover(item, monitor) {
      if (!dropRef.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }
      // Determine rectangle on screen
      const hoverBoundingRect = dropRef.current.getBoundingClientRect()
      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      // Determine mouse position
      const clientOffset = monitor.getClientOffset()
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }
      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }
      // Time to actually perform the action
      moveRow(dragIndex, hoverIndex)
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag, preview] = useDrag({
		type: DND_ITEM_TYPE,
    item: { type: DND_ITEM_TYPE, index },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const opacity = isDragging ? 0 : 1

  preview(drop(dropRef))
  drag(dragRef)

  return (
    <tr ref={dropRef} style={{ opacity }}>
      <td ref={dragRef} style={{cursor: "grab"}}>
        <img src={dragHandleSVG} style={{width: "20px"}} alt=""/>
      </td>
      {row.cells.map(cell => {
        return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
      })}
    </tr>
  )
}

export default function QuizQuestionList(props) {

	const { quizId } = props;

	const [shouldFetch, setShouldFetch] = useState({ fetch: true, silent: false });
	const [loading, setLoading] = useState(true);
  const [quizResult, setQuizResult] = useState({});
  const [quizQuestionList, setQuizQuestionList] = useState([]);
  const [deleteQuestionId, setDeleteQuestionId] = useState("");
  const [showMusicGenerate, setShowMusicGenerate] = useState(false);
  const [pendingMusicGeneration, setPendingMusicGeneration] = useState(false);
  const [showGenerationConfirm, setShowGenerationConfirm] = useState(false);

  function handleMusicGenerated() {
    setShowMusicGenerate(false);
    setPendingMusicGeneration(true);
    setShouldFetch({ fetch: true, silent: true });
  }

  function handleDeleteAbort() {
    setDeleteQuestionId("");
  }

  // =========================
  // Question DELETE callback
  // =========================
  const handleDeletePerform = useCallback(() => {

    setDeleteQuestionId("");

    if (!deleteQuestionId) {
      return;
    }

    const fn = async () => {

      setLoading(true);
      await QuizApi.quizQuestionDelete(quizId, deleteQuestionId);
      setShouldFetch({ fetch: true, silent: false });
    };

    fn();

  }, [ quizId, deleteQuestionId ]);

  // =======================================
  // Effect that fetches quiz question list
  // =======================================
	useEffect(() => {

		if (shouldFetch.fetch) {

			const fetch = async () => {

				setLoading(true);

				const result = await QuizApi.quizDetails(quizId);

        if (result && result.response && result.response.quiz) {

          setQuizResult({
            name: result.response.quiz.name || "",
            type: result.response.quiz.type || "",
            quizGenState: result.response.quiz.quizGenState
          });

          setQuizQuestionList(result.response.questionList || []);
        }
				else {
          setQuizResult({});
          setQuizQuestionList([]);
				}

				setLoading(false);
        setShouldFetch({ fetch: false, silent: false });
			}
	
			fetch();
		}

	}, [shouldFetch, quizId]);

	// ============================
	// Row order update callback
	// ============================
	const rowOrderUpdate = useCallback(tableRecords => {

    const questionIds = tableRecords.map(row => row.id);
    const fn = async () => {

      await QuizApi.quizQuestionOrderUpdate(quizId, questionIds);
    };

    fn();

	}, [quizId]);

  // =========================
  // Table column definitions
  // =========================
  const defaultColumns = useMemo(() => [
    {
      Header: 'Frågetext',
      accessor: 'markdown',
      Cell: row => {
        if (!row || !row.value) {
          return "";
        }
        return row.value.length > 30 ? row.value.slice(0, 30) + "..." : row.value;
      }
    },
    {
      accessor: "id",
      Cell: row => {
        return <div>
          <Link href={absURL(`/admin/quiz/${quizId}/questions/${row.value}`)}>
            <Button variant="outline-secondary" className="btn-sm mr-1">Redigera</Button>
          </Link>
          <Button 
            variant="outline-danger" 
            onClick={() => setDeleteQuestionId(row.value)} 
            className="btn-sm mr-1">
            Ta bort
          </Button>
        </div>;
      }
    }
  ], [quizId]);

  const columns = useMemo(() => 
    {
      if (quizResult.type === "Quiz")
      {
        return [{
          Header: 'Frågelista',
          columns: [
            defaultColumns[0],
            {
              Header: 'Poängtyp',
              accessor: 'type',
              Cell: row => {
                switch(row.value)
                {
                  default:
                    return "";
                  case "Percentage":
                    return "Procentandel";
                  case "AllOrNothing":
                    return "Allt eller inget";
                }
              }
            },
            {
              Header: 'Maxpoäng',
              accessor: 'maxPoints',
            },
            defaultColumns[1]
          ]
        }];
      }
      else 
      {
        return [
          {
            Header: 'Frågelista',
            columns: defaultColumns
          }
        ];
      }
    },
    [defaultColumns, quizResult]
  );

  useEffect(() => {

    if (quizResult && (quizResult.quizGenState === 0 || quizResult.quizGenState === 1))
    {
      const intervalId = setInterval(() => {
        setShouldFetch({ fetch: true, silent: true });
      }, 5000);

      return () => {
        clearInterval(intervalId);
      }
    }

    // Only react to a generation WE just started (not e.g. an old
    // quizGenState=2 already sitting on the quiz when the page loads).
    if (pendingMusicGeneration && quizResult && quizResult.quizGenState === 2)
    {
      setPendingMusicGeneration(false);
      setShowGenerationConfirm(true);
    }
    else if (pendingMusicGeneration && quizResult && quizResult.quizGenState === 3)
    {
      // Failed — the existing red error banner in the list already covers this.
      setPendingMusicGeneration(false);
    }

  }, [quizResult, pendingMusicGeneration]);

  let matchingQuizType = quizTypes.find(t => t.value === quizResult.type);

	return (
		<div id="adminQuizQuestionList">
			<div>

        {(loading && !shouldFetch.silent) && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" />
          </div>
        )}

        {(!loading || (loading && shouldFetch.silent)) && 
          <div>
            <AdminHeader />
            <div className="adminContentWrapper">

              <Button variant="outline-secondary" size="sm" onClick={() => window.history.back()}>
                Tillbaka
              </Button>

              <Link href={absURL(`/admin/quiz/${quizId}/questionCreate`)}>
                <Button size="sm" className="ml-2 mb-3 mt-3 btn-quizPrimaryDark">Ny fråga</Button>
              </Link>

              {quizResult.type === "Musicquiz" &&
                <Button
                  size="sm"
                  className="ml-2 mb-3 mt-3 btn-quizPrimaryDark"
                  disabled={quizResult.quizGenState === 0 || quizResult.quizGenState === 1}
                  onClick={() => setShowMusicGenerate(true)}>
                  {(quizResult.quizGenState === 0 || quizResult.quizGenState === 1)
                    ? "Genererar frågor …"
                    : "Snabbgenerera musikquiz"}
                </Button>
              }

              <div className="d-inline-block ml-2">
                <h6>
                  {quizResult.name} 
                  {matchingQuizType &&
                  <span className='font-italic text-muted ml-1'>
                    ({matchingQuizType.text})
                  </span>
                  }
                </h6>
              </div>

              <div>

                {(quizResult.quizGenState === 0 || quizResult.quizGenState === 1) && 
                  <div className="text-quizPrimary mb-2">
                    <Spinner animation="border" size="sm" className="mr-2" />
                    Väntar på quizgeneratorn
                  </div>
                }
                {(quizQuestionList.length === 0 && quizResult.quizGenState === 3) && 
                  <div className="text-quizError mb-2">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-2" />
                    Quizgeneratorn misslyckades
                  </div>
                }
              </div>

              <Modal show={!!deleteQuestionId} onHide={handleDeleteAbort}>
                <Modal.Header closeButton>
                  <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>Vill du verkligen ta bort frågan?</Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={handleDeleteAbort}>
                    Avbryt
                  </Button>
                  <Button variant="danger" onClick={handleDeletePerform}>
                    Ta bort
                  </Button>
                </Modal.Footer>
              </Modal>

              <MusicQuizGenerateModal
                show={showMusicGenerate}
                onHide={() => setShowMusicGenerate(false)}
                quizId={quizId}
                onGenerated={handleMusicGenerated} />

              <Modal show={showGenerationConfirm} onHide={() => setShowGenerationConfirm(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>Klart!</Modal.Title>
                </Modal.Header>
                <Modal.Body>Frågorna har genererats.</Modal.Body>
                <Modal.Footer>
                  <Button
                    variant="secondary"
                    onClick={() => { setShowGenerationConfirm(false); setShowMusicGenerate(true); }}>
                    Generera om
                  </Button>
                  <Button
                    className="btn-quizPrimaryDark"
                    onClick={() => { window.location.href = absURL('/admin/all'); }}>
                    OK
                  </Button>
                </Modal.Footer>
              </Modal>

              {quizQuestionList.length > 0 &&
                <Table columns={columns} data={quizQuestionList} rowOrderUpdate={rowOrderUpdate} />
              }
            </div>
          </div>
        }
			</div>
		</div>
	);
};